import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FileText, Clock, Search, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import prvImage from "../../assets/images/prv.png";
import cm50Image from "../../assets/images/CM50.jpg";
import RUCImage from "../../assets/images/blog/visittorussia/DSC03649.JPG";
import MediaCard from "../../components/MediaCard";

const PressRelease = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const pressReleases = [
    {
      title: "Rais of Tatarstan Visits Russian University of Cooperation and Launches RUC–Infotech Technopark",
      excerpt: "Rustam Minnikhanov, Dmitry Zubov, and distinguished dignitaries launch new technopark and engage with youth at International Forum on Consumer Cooperation",
      category: "Press Release",
      date: "March 2026",
      link: "/media/ruc-visit",
      image: RUCImage
    },
    {
      title: "Young Indian Cooperator Harsh Shines at CM50 Global Conference",
      excerpt: "Harsh Sanghani represents global youth at CM50 Cooperative Ministers Conference in Doha, advocating for cooperatives as the Third Way of Economy",
      category: "Media Coverage",
      date: "November 2024",
      link: "https://www.indiancooperative.com/featured/young-indian-cooperator-harsh-shines-at-cm50-global-conference/",
      image: "https://www.indiancooperative.com/wp-content/uploads/2025/11/harsh-copy.jpg"
    },
    {
      title: "YuvaSahkar Magazine - 4th Issue, 2nd Year",
      excerpt: "Featured coverage in YuvaSahkar Magazine highlighting youth leadership in cooperatives and global initiatives",
      category: "Media Coverage",
      date: "2024",
      link: "/YuvaSahkar-4th-Issue-2nd-Year-Page16.pdf",
      image: prvImage,
      downloadLink: "/YuvaSahkar-4th-Issue-2nd-Year.pdf"
    }
  ];

  const categories = ["All", ...new Set(pressReleases.map(press => press.category))];

  const filteredPresses = pressReleases.filter(press => {
    const matchesSearch = press.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         press.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || press.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#87212e] via-red-900 to-red-950 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-block mb-6"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl" />
                <FileText className="w-20 h-20 text-white relative" />
              </div>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-6xl md:text-7xl font-bold text-white mb-6 tracking-tight"
            >
              Media
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto mb-12 leading-relaxed"
            >
              Official announcements and media coverage
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="max-w-2xl mx-auto"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl group-hover:bg-white/30 transition-all duration-300" />
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/30 shadow-2xl">
                  <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-white/70 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search media..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-14 pr-6 py-4 rounded-2xl bg-transparent text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 overflow-x-auto">
            <Tag className="w-5 h-5 text-gray-500 flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? "bg-red-600 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section ref={ref} className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPresses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No press releases found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPresses.map((press, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                >
                  <MediaCard {...press} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PressRelease;
